// @wc-ignore-file
// (Route segments are data, not copy.)

/**
 * The one parser behind the path guards (incognito CR-165, contributor
 * CR-183): splits `/workspaces/<slug>/…` into a workspace sub-path or a
 * project page, and fails closed (null) for another workspace, a malformed
 * escape, or an empty / dot / encoded-slash segment. The router hands
 * `beforeLoad` an already-decoded pathname; segments are decoded once more so
 * a stray encoded form can never match a different route than it looks like.
 */

/**
 * Static siblings of `projects/$projectSlug` — the router matches them before
 * the dynamic segment, so they are never a project slug here.
 */
export const RESERVED_PROJECT_SEGMENTS: readonly string[] = Object.freeze(["create"]);

export function isSafeSegment(segment: string): boolean {
  return segment !== "" && segment !== "." && segment !== ".." && !segment.includes("/");
}

export type WorkspacePath =
  | { kind: "workspace"; subpath: string }
  | { kind: "project"; projectSlug: string; segments: string[] };

export function parseWorkspacePath(pathname: string, slug: string): WorkspacePath | null {
  let segments: string[];
  try {
    segments = pathname.split("/").map((segment) => decodeURIComponent(segment));
  } catch {
    return null;
  }

  if (segments.length > 1 && segments[segments.length - 1] === "") {
    segments = segments.slice(0, -1);
  }

  const [leading, root, pathSlug, ...rest] = segments;
  if (leading !== "" || root !== "workspaces" || pathSlug !== slug) {
    return null;
  }

  if (!rest.every(isSafeSegment)) {
    return null;
  }

  const [first, projectSlug, ...projectRest] = rest;
  if (
    first === "projects" &&
    projectSlug !== undefined &&
    !RESERVED_PROJECT_SEGMENTS.includes(projectSlug)
  ) {
    return { kind: "project", projectSlug, segments: projectRest };
  }

  return { kind: "workspace", subpath: rest.join("/") };
}
